import { useState, useEffect } from "react"
import TimeRangeInput from "./TimeRangeInput"
import { Alert } from "@ui/Alert"
import { Button } from "@ui/Button"
import { Card } from "@ui/Card"
import { Input } from "@ui/Input"
import { Switch } from "@ui/Switch"
import CalendarIcon from "../../../icons/cisco-momentum/core/calendar-day-regular.svg?react"
import CloseIcon from "../../../icons/cisco-momentum/core/cancel-regular.svg?react"
import DeleteIcon from "../../../icons/cisco-momentum/core/delete-regular.svg?react"
import WarningIcon from "../../../icons/cisco-momentum/core/warning-regular.svg?react"
import PlusIcon from "../../../icons/cisco-momentum/core/plus-regular.svg?react"

function formatDate(dateStr) {
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric"
  })
}

function makeLocalId() {
  return `l-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export default function ExceptionsManager({ courseId }) {
  const [ cards, setCards ] = useState([])
  const [ selectedDate, setSelectedDate ] = useState("")
  const [ bannerDismissed, setBannerDismissed ] = useState(false)
  const [ loading, setLoading ] = useState(true)
  const [ saving, setSaving ] = useState(false)

  useEffect(() => {
    fetchExceptions()
  }, [])

  async function fetchExceptions() {
    try {
      const res = await fetch(`/lti/courses/${courseId}/office_hours_exceptions`, {
        headers: { "Accept": "application/json" }
      })
      if (res.ok) {
        const data = await res.json()
        setCards(data.exceptions.map(e => ({
          localId: `s-${e.id}`,
          id: e.id,
          date: e.exception_date,
          available: e.exception_type === "available",
          time_ranges: (e.time_ranges && e.time_ranges.length > 0)
            ? e.time_ranges
            : [ { start: "09:00", end: "17:00" } ]
        })))
      }
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  function addCardForDate(date) {
    if (!date) return
    if (cards.some(c => c.date === date)) {
      setSelectedDate("")
      return
    }
    const next = [ ...cards, {
      localId: makeLocalId(),
      id: null,
      date,
      available: true,
      time_ranges: [ { start: "09:00", end: "17:00" } ]
    }].sort((a, b) => a.date.localeCompare(b.date))
    setCards(next)
    setSelectedDate("")
  }

  function updateCard(localId, patch) {
    setCards(prev => prev.map(c => c.localId === localId ? { ...c, ...patch } : c))
  }

  function removeCard(localId) {
    setCards(prev => prev.filter(c => c.localId !== localId))
  }

  function updateRange(localId, ri, newRange) {
    setCards(prev => prev.map(c => c.localId === localId
      ? { ...c, time_ranges: c.time_ranges.map((r, i) => i === ri ? newRange : r) }
      : c))
  }

  function addRange(localId) {
    setCards(prev => prev.map(c => c.localId === localId
      ? { ...c, time_ranges: [ ...c.time_ranges, { start: "09:00", end: "17:00" } ] }
      : c))
  }

  function removeRange(localId, ri) {
    setCards(prev => prev.map(c => c.localId === localId
      ? { ...c, time_ranges: c.time_ranges.filter((_, i) => i !== ri) }
      : c))
  }

  function toast(type, message) {
    window.dispatchEvent(new CustomEvent("app:toast", { detail: { type, message } }))
  }

  async function handleSave() {
    setSaving(true)

    try {
      const res = await fetch(`/lti/courses/${courseId}/office_hours_exceptions/batch_replace`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          exceptions: cards.map(card => ({
            exception_date: card.date,
            exception_type: card.available ? "available" : "unavailable",
            time_ranges: card.available ? card.time_ranges : []
          }))
        })
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.errors?.join(", ") || data.error || "Failed to save")
      }

      await fetchExceptions()
      toast("notice", "Exceptions saved")
    } catch (e) {
      toast("alert", e.message || "Failed to save exceptions")
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="text-sm text-webex-gray-600 dark:text-webex-gray-400">Loading...</div>

  const todayIso = new Date().toISOString().split("T")[0]

  return (
    <div className="space-y-4">
      <Card padding="md" className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-webex-gray-1000 dark:text-white">Exceptions</h3>
          <p className="text-xs text-webex-gray-600 dark:text-webex-gray-400 mt-1">
            Add dates when your availability is different than your regular hours
          </p>
        </div>

        <div>
          <label className="text-xs text-webex-gray-600 dark:text-webex-gray-400 block mb-1">Select date</label>
          <Input
            type="date"
            value={selectedDate}
            min={todayIso}
            onChange={(e) => {
              setSelectedDate(e.target.value)
              addCardForDate(e.target.value)
            }}
          />
        </div>

        {cards.length > 0 && !bannerDismissed && (
          <Alert variant="warning">
            <Alert.Icon>
              <WarningIcon className="w-5 h-5" aria-hidden="true" />
            </Alert.Icon>
            <Alert.Content>
              <Alert.Description>
                Changing your availability on these dates will not affect any already
                scheduled meetings. Reschedule any existing meetings in Webex as necessary.
              </Alert.Description>
            </Alert.Content>
            <Alert.Close onClick={() => setBannerDismissed(true)} />
          </Alert>
        )}

        {cards.map(card => (
          <div key={card.localId} className="bg-webex-gray-50 dark:bg-webex-gray-900 rounded-md px-4 py-3">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-webex-gray-600 dark:text-webex-gray-400" aria-hidden="true" />
                <span className="text-sm font-semibold text-webex-gray-1000 dark:text-white">{formatDate(card.date)}</span>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span className="text-sm text-webex-gray-700 dark:text-webex-gray-300">Available on this day?</span>
                  <Switch
                    checked={card.available}
                    onCheckedChange={(val) => updateCard(card.localId, { available: val })}
                  />
                </label>
                <Button
                  variant="tertiary"
                  size="circle"
                  aria-label="Remove date"
                  onClick={() => removeCard(card.localId)}
                >
                  <CloseIcon className="w-4 h-4" aria-hidden="true" />
                </Button>
              </div>
            </div>

            {card.available && (
              <div className="space-y-2">
                {card.time_ranges.map((range, ri) => (
                  <div key={ri} className="flex items-center gap-2">
                    <TimeRangeInput
                      start={range.start}
                      end={range.end}
                      onChange={(newRange) => updateRange(card.localId, ri, newRange)}
                    />
                    {card.time_ranges.length > 1 && (
                      <Button
                        variant="tertiary"
                        size="circle"
                        aria-label="Remove time range"
                        onClick={() => removeRange(card.localId, ri)}
                      >
                        <DeleteIcon className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="secondary"
                  leadingIcon={<PlusIcon className="w-3.5 h-3.5" aria-hidden="true" />}
                  onClick={() => addRange(card.localId)}
                  className="mt-2"
                >
                  Add availability
                </Button>
              </div>
            )}
          </div>
        ))}
      </Card>

      <Button variant="primary" onClick={handleSave} disabled={saving}>
        {saving ? "Saving..." : "Save"}
      </Button>
    </div>
  )
}
